import styled, { css } from 'styled-components';

import { Typography } from '@/components/ui/Typography/Typography.styles';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { flexBetween, flexColumn, scrollStyles } from '@/styles/common';

type filterOffests = 'margin' | 'padding';

const filterInlineOffesets = (type: filterOffests = 'margin') => {
  return css`
    ${type}-inline: 80px 40px;

    @media (max-width: ${BREAKPOINTS.tablet}) {
      ${type}-inline: 40px;
    }

    @media (max-width: ${BREAKPOINTS.mobile}) {
      ${type}-inline: 16px;
    }
  `;
};

export const Wrapper = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  height: 100%;
  width: 452px;
  background: var(--bg-main);
  color: var(--textAndLabels-main);

  transform: ${({ $isOpen }) =>
    $isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.3s ease;

  ${flexColumn};

  @media (max-width: ${BREAKPOINTS.tablet}) {
    position: fixed;
    z-index: 20;
    width: 100svw;

    transform: ${({ $isOpen }) =>
      $isOpen ? 'translateY(0)' : 'translateY(110%)'};
  }
  will-change: contents, transform;
`;

export const HeaderWrapper = styled.div`
  ${flexColumn};
  ${filterInlineOffesets()}
  position: sticky;
  top: 0;
  z-index: 60;
  background-color: var(--bg-main);

  @media (min-width: ${BREAKPOINTS.tablet}) {
    margin-block: 40px;
  }

  @media (max-width: ${BREAKPOINTS.tablet}) {
    margin-top: 20px;
    margin-bottom: 15px;
  }
  gap: 25px;
`;

export const Header = styled.div`
  ${flexBetween};
  flex-direction: row-reverse;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    flex-direction: row;
  }
`;

export const Content = styled.div`
  flex: 1;
  ${flexColumn}
  gap: 20px;
  max-height: 100%;
  overflow-y: auto;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    display: none;
  }

  & form {
    overflow-y: auto;
    ${scrollStyles}
  }

  & form > * {
    ${filterInlineOffesets()}
  }
`;

export const Footer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  background: var(--bg-main);
  box-shadow: 10px 0px 34px 0px rgba(10, 10, 10, 0.05);
  gap: 0px 20px;
  padding-block: 20px;
  ${filterInlineOffesets('padding')}
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    & svg {
      color: var(--custom-yellow);
    }
  }

  svg {
    width: 18px;
    height: 18px;
    color: var(--textAndLabels-main);
  }

  @media (max-width: ${BREAKPOINTS.tablet}) {
    & svg {
      width: 13px;
      height: 13px;
    }
  }
`;

export const FilterWrapper = styled.div<{ $isOpen: boolean }>`
  background-color: ${({ $isOpen }) => $isOpen && 'var(--bg-card)'};
  border-bottom: 1px solid var(--border-secondary);
  padding: 15px 10px;
  padding-bottom: ${({ $isOpen }) => ($isOpen ? '20px' : '15px')};
  transition: all 0.3s ease;

  &:first-of-type {
    border-top: 1px solid var(--border-secondary);
  }

  @media (max-width: ${BREAKPOINTS.tablet}) {
    padding-inline: 10px;
  }
`;

export const FilterHeader = styled.div`
  ${flexBetween}
  cursor: pointer;
`;

export const FilterTitle = styled(Typography)`
  position: static;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }
`;

export const FilterTexts = styled.div`
  ${flexColumn}
  gap: 5px;
`;

export const SelectedInfo = styled.div`
  display: flex;
  gap: 10px;
`;

export const ResetButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--textAndLabels-main);
`;

export const SelectedOption = styled(Typography).attrs({ variant: 'body3' })`
  color: var(--textAndLabels-secondary);
`;

export const Arrow = styled.div<{
  $isOpen: boolean;
  $isArrowResized: boolean;
}>`
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s ease;

  svg {
    transition: all 0.3s ease;
    ${({ $isOpen, $isArrowResized }) =>
      $isOpen && $isArrowResized
        ? css`
            height: 9.8px;
            width: 16.8px;
          `
        : css`
            height: 7px;
            width: 12px;
          `}
  }
`;

export const FilterContent = styled.div<{ $isOpen: boolean }>`
  overflow: hidden;
  transition: all 0.3s ease;

  ${({ $isOpen }) =>
    $isOpen
      ? css`
          max-height: fit-content;
          opacity: 1;
          margin-top: 20px;
        `
      : css`
          max-height: 0;
          opacity: 0;
          margin-top: 0;
        `}
`;

export const RightSection = styled.div<{ $isVisible: boolean }>`
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
`;
